/**
 * 通道授权：平台把某个通道授权给某个代理，并给出该代理的加价上限。
 *
 * **只有平台能发。** 代理只看得到自己（及其子树）持有的授权 —— 这也是它判断
 * 「我能给商户下发哪些通道」的依据。
 *
 * `max_markup_bps` 是四方模式的核心参数：代理给下级定价时，其相对于自己底价的加价
 * 必须落在这个上限内。后端的校验是**四个定价参数整体算**的（费率、固定费、封底、封顶），
 * 只比 rate_bps 会让固定费成为绕开上限的旁路。
 */

import { useState } from 'react';
import { request, type Collection } from '../lib/api.ts';
import { useAsync, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Button, Card, CopyId, ErrorBox, Field, Modal, Select, TextInput,
} from '../components/ui.tsx';
import { DataTable, type Column } from '../components/table.tsx';
import { formatBps } from '../lib/money.ts';
import { formatDateTime } from '../lib/format.ts';
import type { Channel, Grant, Org } from '../lib/types.ts';

export function GrantsPage() {
  const session = useSession();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Grant | null>(null);

  const list = useAsync<Collection<Grant>>('/v1/grants');
  const canManage = session.isPlatform && session.can('admin');

  const revoke = useMutation(async (id: string) => await request(`/v1/grants/${id}`, { method: 'DELETE' }));

  const columns: readonly Column<Grant>[] = [
    { key: 'org', header: '代理', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.org_name}</span>
        <CopyId id={row.org_id} />
      </div>
    ) },
    { key: 'channel', header: '通道', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.channel_code}</span>
        <span className="cell-muted" style={{ fontSize: 11 }}>{row.channel_label}</span>
      </div>
    ) },
    { key: 'markup', header: '加价上限', numeric: true, render: (row) => (
      <span className="num cell-strong">{formatBps(row.max_markup_bps)}</span>
    ) },
    { key: 'created', header: '授权时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.created_at)}</span>
    ) },
    ...(canManage ? [{
      key: 'actions',
      header: '',
      render: (row: Grant) => (
        <div className="row tight">
          <Button size="sm" onClick={() => { setEditing(row); }}>改上限</Button>
          <Button
            size="sm"
            variant="danger"
            disabled={revoke.pending}
            onClick={() => {
              void revoke.run(row.id).then((result) => { if (result !== null) list.reload(); });
            }}
          >
            撤销
          </Button>
        </div>
      ),
    }] : []),
  ];

  return (
    <>
      <div className="row">
        <h1>通道授权</h1>
        <span style={{ flex: 1 }} />
        {canManage && <Button variant="primary" onClick={() => { setCreating(true); }}>授权给代理</Button>}
      </div>

      <ErrorBox error={revoke.error} />

      {!session.isPlatform && (
        <div className="alert" data-tone="info">
          <span className="alert-title">这是你持有的通道授权</span>
          <span>
            加价上限决定了你给下级定价时最多能在自己的底价上加多少。
            超出上限的定价会被后端直接拒绝 —— 费率、固定费、封底、封顶四个参数是整体校验的。
          </span>
        </div>
      )}

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.data?.data ?? []}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          empty="还没有任何通道授权"
          emptyHint="授权只能发给代理组织。要给商户开通道，请用「MID 下发」。"
        />
      </Card>

      {creating && (
        <CreateGrantModal
          onClose={() => { setCreating(false); }}
          onDone={() => { setCreating(false); list.reload(); }}
        />
      )}
      {editing !== null && (
        <EditGrantModal
          grant={editing}
          onClose={() => { setEditing(null); }}
          onDone={() => { setEditing(null); list.reload(); }}
        />
      )}
    </>
  );
}

function CreateGrantModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [orgId, setOrgId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [markup, setMarkup] = useState('100');

  const agents = useAsync<{ data: readonly Org[] }>('/v1/orgs', { query: { type: 'agent', status: 'active', limit: 200 } });
  const channels = useAsync<Collection<Channel>>('/v1/channels');

  const create = useMutation(async () => await request<Grant>('/v1/grants', {
    method: 'POST',
    body: {
      org_id: orgId,
      channel_account_id: channelId,
      max_markup_bps: Number(markup),
    },
  }));

  return (
    <Modal
      title="授权通道给代理"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={create.pending || orgId === '' || channelId === ''}
            onClick={() => { void create.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {create.pending ? '授权中…' : '授权'}
          </Button>
        </>
      }
    >
      <ErrorBox error={create.error} />

      <Field label="代理" hint="只能授权给 agent 类型的组织。给商户开通道请用 MID 下发。">
        <Select
          value={orgId}
          onChange={setOrgId}
          placeholder="选择代理"
          options={(agents.data?.data ?? []).map((org) => ({ value: org.id, label: org.name }))}
        />
      </Field>

      <Field label="通道账户">
        <Select
          value={channelId}
          onChange={setChannelId}
          placeholder="选择通道"
          options={(channels.data?.data ?? []).map((channel) => ({
            value: channel.id,
            label: `${channel.channel_code} · ${channel.label} · ${channel.mode}`,
          }))}
        />
      </Field>

      <Field
        label="加价上限（基点）"
        hint={`当前 ${formatBps(Number(markup) || 0)}。100 基点 = 1%。这是代理在自己底价之上最多能加的幅度。`}
      >
        <TextInput value={markup} onChange={setMarkup} numeric />
      </Field>
    </Modal>
  );
}

function EditGrantModal({
  grant, onClose, onDone,
}: {
  grant: Grant;
  onClose: () => void;
  onDone: () => void;
}) {
  const [markup, setMarkup] = useState(String(grant.max_markup_bps));

  const save = useMutation(async () => await request<Grant>(`/v1/grants/${grant.id}`, {
    method: 'PATCH',
    body: { max_markup_bps: Number(markup) },
  }));

  return (
    <Modal
      title={`调整加价上限 · ${grant.org_name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={save.pending || Number(markup) === grant.max_markup_bps}
            onClick={() => { void save.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {save.pending ? '保存中…' : '保存'}
          </Button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <p className="field-hint">
        通道：{grant.channel_code} · {grant.channel_label}
      </p>
      <Field label="加价上限（基点）" hint={`当前 ${formatBps(Number(markup) || 0)}`}>
        <TextInput value={markup} onChange={setMarkup} numeric />
      </Field>
      <div className="alert" data-tone="info">
        <span className="alert-title">调低上限不会追溯已有费率</span>
        <span>费率规则是追加写的，历史规则原样保留。调低只影响此后新建的定价。</span>
      </div>
    </Modal>
  );
}
